import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMenu } from '../MenuContext';
import { useUser } from '../UserContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { MenuItem } from 'types/menuTypes';
import { logger } from 'utils/logger';
import {
  extractDocumentId,
  findFirstValidMenu,
  findMenuPathByDocumentId,
  findMenuPathByUrl,
  findSmartFallback,
} from 'utils/menuUtils';

interface MenuSelectionType {
  firstLevelMenu: MenuItem | null;
  secondLevelMenu: MenuItem | null;
  thirdLevelMenu: MenuItem | null;
  setFirstLevelMenu: (menu: MenuItem | null) => void;
  setSecondLevelMenu: (menu: MenuItem | null) => void;
  setThirdLevelMenu: (menu: MenuItem | null) => void;
  selectMenuByDocumentId: (
    documentId: string,
    shouldNavigate?: boolean,
    pathOverride?: MenuItem[],
    path?: string,
    preserveLevel?: boolean
  ) => void;
  selectMenuByPath: (path: string) => void;
  currentLevel: 'first' | 'second' | 'third' | null;
  activeModulePath: string | null;
  activeDocumentId: string | null;
  isInitialized: boolean;
}

const MenuSelectionContext = createContext<
  | (MenuSelectionType & {
      isModalSubMenuOpen: boolean;
      setIsModalSubMenuOpen: (open: boolean) => void;
    })
  | undefined
>(undefined);

/**
 * Checks if the first item in a menu path represents a meeting room,
 * and applies the special three-level selection pattern for meeting rooms.
 * Returns true if the meeting room pattern was applied.
 */
function applyMeetingRoomMenuSelection(
  items: MenuItem[],
  setFirst: (m: MenuItem | null) => void,
  setSecond: (m: MenuItem | null) => void,
  setThird: (m: MenuItem | null) => void,
  shouldNavigate: boolean,
  navigateFn: (path: string) => void
): boolean {
  const isMeetingRoom = items[0]?.feature?.dynamic_component === 'MEETING_ROOM';

  if (!isMeetingRoom || !items[1]) return false;

  // For meeting rooms: parent stays as first+second, room becomes third
  setFirst(items[0]);
  setSecond(items[0]);
  setThird(items[1]);

  if (shouldNavigate && items[1]?.path) {
    logger.debug('[MenuSelectionContext] Navigating to:', { data: items[1].path });
    navigateFn(items[1].path);
  }

  return true;
}

export const MenuSelectionProvider = ({ children }: { children: React.ReactNode }) => {
  const { menu } = useMenu();
  const { token } = useUser();
  const menuRoutes = useMenuRoutes();
  const navigate = useNavigate();
  const isInitializing = useRef(false);

  // Refs for values accessed inside useCallback to avoid stale closures
  const menuRef = useRef(menu);
  menuRef.current = menu;
  const menuRoutesRef = useRef(menuRoutes);
  menuRoutesRef.current = menuRoutes;

  const [firstLevelMenu, setFirstLevelMenu] = useState<MenuItem | null>(null);
  const [secondLevelMenu, setSecondLevelMenu] = useState<MenuItem | null>(null);
  const [thirdLevelMenu, setThirdLevelMenu] = useState<MenuItem | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Global ModalSubMenu state, restored from localStorage
  const [isModalSubMenuOpen, setIsModalSubMenuOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem('selectedMenus');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return !!parsed.hasSecondLevel;
      } catch {
        return false;
      }
    }
    return false;
  });

  // Whenever the second level menu changes, update the modal state
  React.useEffect(() => {
    setIsModalSubMenuOpen(!!secondLevelMenu);
  }, [secondLevelMenu]);

  // Simplified derived values
  const currentLevel = useMemo((): 'first' | 'second' | 'third' | null => {
    if (thirdLevelMenu) return 'third';
    if (secondLevelMenu) return 'second';
    if (firstLevelMenu) return 'first';
    return null;
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  const currentLevelRef = useRef<'first' | 'second' | 'third' | null>(currentLevel);
  currentLevelRef.current = currentLevel;

  const activeModulePath = useMemo(() => {
    const lastMenu = thirdLevelMenu || secondLevelMenu || firstLevelMenu;
    return lastMenu?.path || null;
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  const activeDocumentId = useMemo(() => {
    const lastMenu = thirdLevelMenu || secondLevelMenu || firstLevelMenu;
    if (!lastMenu) return null;

    return extractDocumentId(lastMenu);
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  const activeDocumentIdRef = useRef(activeDocumentId);
  activeDocumentIdRef.current = activeDocumentId;

  // Save to localStorage only the data needed for restoration
  React.useEffect(() => {
    localStorage.setItem(
      'selectedMenus',
      JSON.stringify({
        activeDocumentId,
        hasSecondLevel: !!secondLevelMenu,
        isModalSubMenuOpen,
      })
    );
  }, [activeDocumentId, secondLevelMenu, isModalSubMenuOpen]);

  // Select menu by documentId
  const selectMenuByDocumentId = useCallback(
    (
      documentId: string,
      shouldNavigate = false,
      pathOverride?: MenuItem[],
      path?: string,
      preserveLevel?: boolean
    ) => {
      const currentMenu = menuRef.current;

      if (!currentMenu.length || !documentId) {
        logger.debug('[MenuSelectionContext] Empty menu or invalid documentId');
        return;
      }

      logger.debug('[MenuSelectionContext] Selecting menu:', {
        documentId,
        shouldNavigate,
        hasPathOverride: !!pathOverride,
        path,
        preserveLevel,
        currentActiveDocumentId: activeDocumentIdRef.current,
        currentLevel: currentLevelRef.current,
      });

      // If we have a pathOverride, use it directly
      if (pathOverride) {
        logger.debug(
          '[MenuSelectionContext] Using pathOverride:',
          pathOverride.map(m => m.label)
        );

        // Check if it's a meeting room and apply special selection
        if (
          applyMeetingRoomMenuSelection(
            pathOverride,
            setFirstLevelMenu,
            setSecondLevelMenu,
            setThirdLevelMenu,
            shouldNavigate,
            navigate
          )
        ) {
          return;
        }

        // For other menus, keep the normal behavior
        setFirstLevelMenu(pathOverride[0] || null);
        setSecondLevelMenu(pathOverride[1] || null);
        setThirdLevelMenu(pathOverride[2] || null);

        if (shouldNavigate) {
          const lastMenu = pathOverride[2] || pathOverride[1] || pathOverride[0];
          if (lastMenu?.path) {
            logger.debug('[MenuSelectionContext] Navigating to:', { data: lastMenu.path });
            navigate(lastMenu.path);
          }
        }
        return;
      }

      logger.debug('[MenuSelectionContext] Searching menu with findMenuPathByDocumentId:', {
        documentId,
        path,
      });
      const menuPath = findMenuPathByDocumentId(currentMenu, documentId, path);
      if (!menuPath) {
        logger.warn('[MenuSelectionContext] Menu not found:', documentId, path);
        return;
      }

      logger.debug(
        '[MenuSelectionContext] Menu found:',
        menuPath.items.map(m => m.label)
      );

      // Check if it's a meeting room and apply special selection
      if (
        applyMeetingRoomMenuSelection(
          menuPath.items,
          setFirstLevelMenu,
          setSecondLevelMenu,
          setThirdLevelMenu,
          shouldNavigate,
          navigate
        )
      ) {
        return;
      }

      // For other menus, preserve the current level if requested
      const targetLevel = menuPath.items.length;
      const currentLevelNumber =
        currentLevelRef.current === 'first'
          ? 1
          : currentLevelRef.current === 'second'
            ? 2
            : currentLevelRef.current === 'third'
              ? 3
              : 0;

      logger.debug('[MenuSelectionContext] Levels:', {
        targetLevel,
        currentLevelNumber,
        currentLevel: currentLevelRef.current,
        menuPathLength: menuPath.items.length,
        preserveLevel,
      });

      // If preserveLevel is true, keep the current level and only update the activeDocumentId
      if (preserveLevel) {
        logger.debug(
          '[MenuSelectionContext] Preserving current level as requested:',
          currentLevelRef.current
        );
        // Don't change the menus, just ensure the activeDocumentId is updated
        return;
      }

      // Check if the current path is already correct to avoid unnecessary changes
      const lastMenu = menuPath.items[menuPath.items.length - 1];
      const currentPath = decodeURIComponent(window.location.pathname);

      if (lastMenu?.path === currentPath && !shouldNavigate) {
        logger.debug(
          '[MenuSelectionContext] Current path is already correct, skipping unnecessary changes'
        );
        return;
      }

      // For other menus, ALWAYS set the correct menus based on the found path
      setFirstLevelMenu(menuPath.items[0] || null);
      setSecondLevelMenu(menuPath.items[1] || null);
      setThirdLevelMenu(menuPath.items[2] || null);

      // Navigate if needed
      if (shouldNavigate) {
        if (lastMenu?.path) {
          logger.debug('[MenuSelectionContext] Navigating to:', { data: lastMenu.path });
          navigate(lastMenu.path);
        }
      }
    },
    [navigate]
  );

  // Select menu by path
  const selectMenuByPath = useCallback(
    (path: string) => {
      const currentMenu = menuRef.current;

      if (!currentMenu.length || !path) {
        logger.debug('[MenuSelectionContext] Empty menu or invalid path');
        return;
      }

      logger.debug('[MenuSelectionContext] Selecting menu by path:', { data: path });

      const menuPath = findMenuPathByUrl(currentMenu, path);
      if (!menuPath) {
        logger.warn('[MenuSelectionContext] Menu not found for path:', { data: path });
        return;
      }

      setFirstLevelMenu(menuPath.items[0] || null);
      setSecondLevelMenu(menuPath.items[1] || null);
      setThirdLevelMenu(menuPath.items[2] || null);
    },
    []
  );

  // Debug log for selected menus
  useEffect(() => {
    logger.debug('[MenuSelectionContext] Selected menus:', {
      firstLevel: firstLevelMenu?.label,
      secondLevel: secondLevelMenu?.label,
      thirdLevel: thirdLevelMenu?.label,
      currentLevel,
      activeDocumentId,
    });
  }, [firstLevelMenu, secondLevelMenu, thirdLevelMenu, currentLevel, activeDocumentId]);

  // Simplified initialization
  useEffect(() => {
    if (!token || !menu.length || isInitialized || isInitializing.current) return;

    isInitializing.current = true;
    logger.debug('[MenuSelectionContext] Initializing...', {
      currentPath: window.location.pathname,
      menuLength: menu.length,
      isInitialized,
    });

    // Wait briefly to ensure UserContext has finished initializing
    const initializeWithDelay = () => {
      const currentPath = decodeURIComponent(window.location.pathname);

      // Skip initialization for event-invite paths and embedded form paths
      const isEventInvitePath = currentPath.startsWith('/invite/event/');
      const isEmbeddedFormPath = currentPath.startsWith('/form/');
      if (isEventInvitePath || isEmbeddedFormPath) {
        logger.debug('[MenuSelectionContext] Special path detected, skipping initialization:', {
          isEventInvitePath,
          isEmbeddedFormPath,
          currentPath,
        });
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // Root path: mark initialized immediately so RouteGuard can redirect
      // to the first valid menu route. No menu selection needed on /.
      if (currentPath === '/') {
        logger.debug('[MenuSelectionContext] Root path, marking initialized for RouteGuard redirect');
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // 1. Try to restore by current URL path
      const menuPathByUrl = findMenuPathByUrl(menu, currentPath);
      if (menuPathByUrl) {
        logger.debug('[MenuSelectionContext] Restoring by URL path:', { data: currentPath });
        selectMenuByPath(currentPath);
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // 2. Try to restore from localStorage
      const stored = localStorage.getItem('selectedMenus');
      if (stored) {
        try {
          const { activeDocumentId: storedId } = JSON.parse(stored);
          if (storedId) {
            logger.debug('[MenuSelectionContext] Restoring from localStorage:', { data: storedId });
            selectMenuByDocumentId(storedId, false);
            setIsInitialized(true);
            isInitializing.current = false;
            return;
          }
        } catch (e) {
          logger.warn('[MenuSelectionContext] Error restoring from localStorage:', { data: e });
        }
      }

      // 3. Check if the current route is valid before using fallback
      const currentMenuRoutes = menuRoutesRef.current;
      const isCurrentRouteValid =
        currentMenuRoutes.length > 0 && currentMenuRoutes.some(route => route.path === currentPath);
      if (isCurrentRouteValid) {
        logger.debug(
          '[MenuSelectionContext] Current route is valid, waiting for automatic sync'
        );
        setIsInitialized(true);
        isInitializing.current = false;
        return;
      }

      // 4. If nothing was found and the route is invalid, use smart fallback
      const fallback = findSmartFallback(menu, currentPath, currentLevelRef.current);
      if (fallback) {
        logger.debug('[MenuSelectionContext] Using smart fallback:', {
          data: fallback.path,
        });
        selectMenuByDocumentId(fallback.documentId, false, fallback.items, fallback.path);
      } else {
        // Last resort: first valid menu
        const firstValid = findFirstValidMenu(menu);
        if (firstValid) {
          const documentId = extractDocumentId(firstValid);
          if (documentId) {
            logger.debug('[MenuSelectionContext] Using first valid menu:', {
              data: firstValid.label,
            });
            selectMenuByDocumentId(documentId, false);
          }
        }
      }

      // ALWAYS mark initialized to prevent deadlock — even if no menu item
      // was selected, RouteGuard will handle navigation to a valid route
      setIsInitialized(true);
      isInitializing.current = false;
    };

    // Run synchronously — the effect guard (!token || !menu.length) already
    // ensures prerequisites are met, so no artificial delay is needed.
    initializeWithDelay();
  }, [menu, isInitialized, token, selectMenuByDocumentId, selectMenuByPath]);

  const value = useMemo(
    () => ({
      firstLevelMenu,
      secondLevelMenu,
      thirdLevelMenu,
      setFirstLevelMenu,
      setSecondLevelMenu,
      setThirdLevelMenu,
      selectMenuByDocumentId,
      selectMenuByPath,
      currentLevel,
      activeModulePath,
      activeDocumentId,
      isInitialized,
      isModalSubMenuOpen,
      setIsModalSubMenuOpen,
    }),
    [
      firstLevelMenu,
      secondLevelMenu,
      thirdLevelMenu,
      selectMenuByDocumentId,
      selectMenuByPath,
      currentLevel,
      activeModulePath,
      activeDocumentId,
      isInitialized,
      isModalSubMenuOpen,
    ]
  );

  return <MenuSelectionContext.Provider value={value}>{children}</MenuSelectionContext.Provider>;
};

export const useMenuSelection = (): MenuSelectionType => {
  const context = useContext(MenuSelectionContext);
  if (!context) {
    throw new Error('useMenuSelection must be used within a MenuSelectionProvider');
  }
  return context;
};
